/**
 * LinkIssueModal Component
 *
 * Modal dialog for creating links between issues
 * Includes issue search and link type selection
 */

import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, LinkIcon } from '@heroicons/react/24/outline';
import enhancedApiService from '../../api/enhancedApiService';
import { useUI } from '../../contexts/UIContext';

const LinkIssueModal = ({ isOpen, onClose, currentIssueId, onLinkCreated }) => {
  const [linkType, setLinkType] = useState('related');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [allIssues, setAllIssues] = useState([]);
  const searchInputRef = useRef(null);
  const { showNotification } = useUI();

  const linkTypes = [
    { value: 'related', label: 'Related To', description: 'General relationship' },
    { value: 'duplicate', label: 'Duplicate', description: 'Same issue reported multiple times' },
    { value: 'blocks', label: 'Blocks', description: 'This issue blocks another' },
    { value: 'blocked_by', label: 'Blocked By', description: 'This issue is blocked by another' },
    { value: 'caused_by', label: 'Caused By', description: 'This issue was caused by another' },
    { value: 'causes', label: 'Causes', description: 'This issue causes another' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadAllIssues();
      // Focus search input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Reset state when modal closes
      setSearchQuery('');
      setSelectedIssue(null);
      setLinkType('related');
      setSearchResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter issues based on search query
    if (searchQuery.trim() === '') {
      setSearchResults(allIssues.slice(0, 10)); // Show first 10 by default
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = allIssues.filter((issue) => {
        const titleMatch = issue.title?.toLowerCase().includes(query);
        const idMatch = issue.id?.toLowerCase().includes(query);
        const descMatch = issue.description?.toLowerCase().includes(query);
        return titleMatch || idMatch || descMatch;
      }).slice(0, 10); // Limit to 10 results
      setSearchResults(filtered);
    }
  }, [searchQuery, allIssues]);

  const loadAllIssues = async () => {
    try {
      setLoading(true);
      const response = await enhancedApiService({
        method: 'GET',
        url: '/issues'
      });

      if (response.success && response.data?.issues) {
        // Flatten all priority levels into single array
        const issues = [
          ...(response.data.issues.high_priority || []),
          ...(response.data.issues.medium_priority || []),
          ...(response.data.issues.low_priority || [])
        ];

        // Filter out the current issue
        const filteredIssues = issues.filter((issue) => issue.id !== currentIssueId);
        setAllIssues(filteredIssues);
        setSearchResults(filteredIssues.slice(0, 10));
      }
    } catch (error) {
      console.error('Error loading issues:', error);
      showNotification('Failed to load issues', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!selectedIssue) {
      showNotification('Please select an issue to link', 'error');
      return;
    }

    try {
      setCreating(true);
      const response = await enhancedApiService({
        method: 'POST',
        url: `/issues/${currentIssueId}/links`,
        data: {
          target_issue_id: selectedIssue.id,
          link_type: linkType
        }
      });

      if (response.success) {
        showNotification('Issue linked successfully', 'success');
        if (onLinkCreated) {
          onLinkCreated();
        }
        onClose();
      }
    } catch (error) {
      console.error('Error creating link:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create link';
      showNotification(errorMsg, 'error');
    } finally {
      setCreating(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[severity] || colors.medium}`}>
        {severity}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LinkIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Link Issue</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-4 space-y-6">
            {/* Link Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Type
              </label>
              <select
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {linkTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Issue Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search for Issue
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by issue title or ID..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Search Results */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Issue {selectedIssue && <span className="text-green-600">(1 selected)</span>}
              </label>
              <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading issues...</span>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No issues found</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {searchResults.map((issue) => (
                      <button
                        key={issue.id}
                        onClick={() => setSelectedIssue(issue)}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${
                          selectedIssue?.id === issue.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {issue.title}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                #{issue.id?.split('-')[0]}
                              </span>
                              {getSeverityBadge(issue.severity)}
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                issue.status === 'resolved'
                                  ? 'bg-green-100 text-green-700'
                                  : issue.status === 'in_progress'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {issue.status || 'unknown'}
                              </span>
                            </div>
                          </div>
                          {selectedIssue?.id === issue.id && (
                            <div className="ml-2 flex-shrink-0">
                              <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center">
                                <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                  <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateLink}
              disabled={!selectedIssue || creating}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </span>
              ) : (
                'Create Link'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkIssueModal;
